/* eslint-disable camelcase */
import { useSearchParams } from 'react-router-dom';

import {
  PlotGroup,
  PlotGroupFilters,
  PlotGroupFiltersEnum,
  PlotGroupGroupBy,
  PlotGroupSort,
  PlotLabelValue,
} from '@/api/rest/resources/types/plotGroup';
import { useCreatePlotGroup, useUpdatePlotGroup } from '@/pages/shared/hooks/usePlotGroup';
import { isNullish } from '@/utils/booleans';

import {
  useActivePlotGroup,
  useDetailsPlotGroup,
  usePlotGroupContext,
  usePlotGroupDefault,
  usePlotGroupNames,
  usePlotGroupWithoutGrouping,
  useSetPlotGroup,
  useSetPlotGroupId,
} from '../PlotGroupsContextProvider';

export const useAddPlotGroup = () => {
  const { addPlotGroup } = useSetPlotGroup();
  const { DEFAULT } = usePlotGroupNames();

  const { submit: submitPost } = useCreatePlotGroup();
  const { submit: submitUpdate } = useUpdatePlotGroup();

  const { setPlotGroupWithoutGrouping } = useSetPlotGroupWithoutGrouping();

  const setPlotGroup = async (plotGroup: PlotGroup, resetPlotGroups?: boolean) => {
    addPlotGroup(plotGroup, resetPlotGroups);

    let response: { id: string } | null = null;
    if (plotGroup.id && plotGroup.name === DEFAULT) {
      response = await submitUpdate(plotGroup, plotGroup.id);
    } else {
      response = await submitPost(plotGroup);
    }

    if (plotGroup.name === DEFAULT) {
      setPlotGroupWithoutGrouping(plotGroup);
    }

    return response?.id ?? null;
  };

  return { setPlotGroup };
};

export const useSetPlotGroupFilters = () => {
  const activePlotGroup = useActivePlotGroup();
  const detailsPlotGroup = useDetailsPlotGroup();
  const { setPlotGroup } = useAddPlotGroup();
  const { setDetailsPlotGroup } = useSetDetailsPlotGroup();
  const { setPlotGroups, setTransientPlotGroupId } = usePlotGroupContext();
  const [, setSearchParams] = useSearchParams();

  const defaultPlotGroup = usePlotGroupDefault('default');

  const { DEFAULT, NO_GROUPING, DETAILS } = usePlotGroupNames();

  const setPlotGroupFilters = async ({
    filters,
    groupBy,
    additional_plot_ids,
  }: {
    filters: PlotGroupFilters;
    groupBy: PlotGroupGroupBy;
    additional_plot_ids?: string[];
  }) => {
    const hasPlotGroupCustomName =
      activePlotGroup.name !== null && ![DEFAULT, NO_GROUPING, DETAILS].includes(activePlotGroup.name);

    const hasActiveFilters = Object.values(filters).some(
      (f) => !isNullish(f) && (Array.isArray(f) ? f.length > 0 : true),
    );
    const hasActiveGroupBy = Object.values(groupBy).some((g) => !isNullish(g));
    const hasAdditionalPlots = Array.isArray(additional_plot_ids) && additional_plot_ids.length > 0;
    const hasFilters = hasActiveFilters || hasActiveGroupBy || hasAdditionalPlots;

    if (hasPlotGroupCustomName) {
      // Update in-memory filters on the named group so the filter pills and
      // form default values reflect the new selection immediately.
      setPlotGroups((prev) => prev.map((pg) => (pg.active ? { ...pg, filters, groupBy, additional_plot_ids } : pg)));

      // Write the transient filters to the DETAILS group in the DB so the
      // backend can filter plots/map/widgets by them. The named group is
      // never mutated. transientPlotGroupId overrides the URL param for all
      // API data-fetching calls (via usePlotGroupId).
      if (hasFilters) {
        const newDetailsGroup = { ...detailsPlotGroup, filters, groupBy, additional_plot_ids };
        const { id } = await setDetailsPlotGroup(newDetailsGroup, true);
        setTransientPlotGroupId(id);
        setSearchParams((prev) => {
          prev.set('plotGroupId', id); // Activate details plot group in the URL
          return prev;
        });
      } else {
        // All filters cleared — fall back to the original named group for API calls.
        setTransientPlotGroupId(null);
      }
      return;
    }

    const newPlotGroup = { ...activePlotGroup, filters, groupBy, additional_plot_ids };
    await setPlotGroup(newPlotGroup);

    if (hasFilters) {
      return;
    }

    setSearchParams((prev) => {
      prev.delete('plotGroupId');
      return prev;
    });
  };

  const setDefaultPlotGroup = () => {
    setTransientPlotGroupId(null);
    setPlotGroup(defaultPlotGroup);
  };

  return { setPlotGroupFilters, setDefaultPlotGroup };
};

export const useSetDetailsPlotGroup = () => {
  const detailsPlotGroup = useDetailsPlotGroup();
  const { addPlotGroup } = useSetPlotGroup();

  const { submit: submitPost } = useCreatePlotGroup();
  const { submit: submitUpdate } = useUpdatePlotGroup();

  const { DETAILS } = usePlotGroupNames();
  const { setPlotGroupWithoutGrouping } = useSetPlotGroupWithoutGrouping();

  const setDetailsPlotGroup = (plotGroup: PlotGroup, allowGrouping = false) => {
    addPlotGroup(plotGroup);

    setPlotGroupWithoutGrouping(plotGroup); // Match no grouping plot group to details plot group filters
    if (plotGroup.id && plotGroup.name === DETAILS) {
      return submitUpdate(plotGroup, plotGroup.id, undefined, allowGrouping);
    }

    return submitPost({ ...plotGroup, name: DETAILS }, undefined, undefined, allowGrouping);
  };

  const setSecondaryPlotGroupPlotLabel = (plotLabels: PlotLabelValue[]) => {
    const newDetailsPlotGroup = {
      ...detailsPlotGroup,
      filters: {
        ...detailsPlotGroup.filters,
        [PlotGroupFiltersEnum.plot__labels__id]: plotLabels,
      },
    };

    return setDetailsPlotGroup(newDetailsPlotGroup);
  };

  return { setDetailsPlotGroup, setSecondaryPlotGroupPlotLabel };
};

export const useSetPlotGroupSort = () => {
  const activePlotGroup = useActivePlotGroup();
  const { setPlotGroup } = useAddPlotGroup();
  const { setActivePlotGroupId } = useSetPlotGroupId();

  const setPlotGroupSort = async (sort: PlotGroupSort) => {
    const newPlotGroup = { ...activePlotGroup, sort };

    const newId = await setPlotGroup(newPlotGroup);

    if (newId) {
      setActivePlotGroupId(newId);
    }
  };

  return { setPlotGroupSort };
};

// TODO: MVP-CW the only reason to have this feature, is because we need the plots of a group after applying the grouping. The BE still
// needs to build this logic. When done, all the no-grouping plot group logic should be removed and replaced by it.
export const useSetPlotGroupWithoutGrouping = () => {
  const { submit: submitPost } = useCreatePlotGroup();
  const { submit: submitUpdate } = useUpdatePlotGroup();

  const plotGroupWithoutGrouping = usePlotGroupWithoutGrouping();
  const { NO_GROUPING } = usePlotGroupNames();

  const { setPlotGroups } = usePlotGroupContext();

  const setPlotGroupWithoutGrouping = async (plotGroup: PlotGroup) => {
    const newPlotGroupWithoutGrouping = {
      ...plotGroup,
      id: plotGroupWithoutGrouping?.id ?? null,
      active: false,
      name: NO_GROUPING,
    };

    if (newPlotGroupWithoutGrouping.id) {
      submitUpdate(newPlotGroupWithoutGrouping, newPlotGroupWithoutGrouping.id);
    } else {
      const { id } = await submitPost(newPlotGroupWithoutGrouping, undefined, false);
      newPlotGroupWithoutGrouping.id = id;
    }

    setPlotGroups((prev) => [...prev, newPlotGroupWithoutGrouping]);
  };

  return { setPlotGroupWithoutGrouping };
};
